import axios from 'axios';
import {getSession, signOut} from './supabase';
import { getCacheItem, setCacheItem, clearCacheItem } from '../utils/cacheUtils';
import { getRequestAbortController, requestCompleted } from '../utils/requestUtils';
import { debugLog, debugWarn, debugError, debugAPI } from '../utils/debugLogger';
import { getCacheStrategy, DATA_TYPE_MAPPING } from '../utils/cacheStrategies';
import { buildDedupKey, dedupedRequest } from '../utils/requestDedup';


// Debug session checking on load (development only) - OPTIMIZED
if (process.env.NODE_ENV === 'development') {
  (async () => {
    try {
      const session = await getSession();
      if (session?.access_token) {
        debugLog("Auth session initialized");
      } else {
        debugLog("No valid auth session found");
      }
    } catch (e) {
      debugError("Auth session error:", e.message);
    }
  })();
}

// Create a base API client with defaults
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/api'  // Local development
  : process.env.REACT_APP_API_BASE_URL || 'https://sparvi-webapp-fjdjdvh2bse9d0gm.centralus-01.azurewebsites.net/api';

// Environment details logged once on startup (development only)
debugLog("API Config:", { baseURL: API_BASE_URL });

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Enhanced auth ready state management
let authReady = false;
let authReadyPromise = null;
let authReadyResolve = null;
let authReadyReject = null;

// Initialize the auth ready promise
const initAuthReadyPromise = () => {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve, reject) => {
      authReadyResolve = resolve;
      authReadyReject = reject;
    });
  }
  return authReadyPromise;
};

const isCancelledRequest = (error) => {
  return axios.isCancel(error) ||
         error.name === 'CanceledError' ||
         error.cancelled === true;
};

// Enhanced function to check if auth is ready
export const waitForAuth = async (timeoutMs = 5000) => {
  if (authReady) {
    return true;
  }

  // Initialize the promise if it doesn't exist
  const promise = initAuthReadyPromise();

  // Add timeout
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('Auth ready timeout'));
    }, timeoutMs);
  });

  try {
    await Promise.race([promise, timeoutPromise]);
    return true;
  } catch (error) {
    // Try to check session directly as fallback
    try {
      const session = await getSession();
      if (session?.access_token) {
        setAuthReady(); // Set it ready now
        return true;
      }
    } catch (sessionError) {
      // Silent failure - auth not ready
    }

    return false;
  }
};

// Enhanced function to set auth as ready
export const setAuthReady = () => {
  authReady = true;
  if (authReadyResolve) {
    authReadyResolve(true);
    // Reset the promise for future use
    authReadyPromise = null;
    authReadyResolve = null;
    authReadyReject = null;
  }
};

// Function to reset auth ready state (e.g., on logout)
export const resetAuthReady = () => {
  authReady = false;
  if (authReadyReject) {
    authReadyReject(new Error('Auth reset'));
  }
  authReadyPromise = null;
  authReadyResolve = null;
  authReadyReject = null;
};

// Silent auth initialization
(async () => {
  try {
    const session = await getSession();
    if (session?.access_token) {
      setAuthReady();
    }
  } catch (e) {
    // Silent failure
  }
})();

// Enhanced request interceptor with better token handling
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const session = await getSession();

      const token = session?.access_token;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        debugAPI('REQUEST', config.url, 'authenticated');
      } else {
        debugWarn(`No auth token available for ${config.url}`);
      }
    } catch (err) {
      debugError('[API Request] Error adding auth token:', err);
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Enhanced response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't treat cancelled requests as errors
    if (axios.isCancel(error)) {
      return Promise.reject({ cancelled: true });
    }

    // Handle specific error codes
    if (error.response) {
      const { status } = error.response;

      if (status === 401) {
        // Prevent multiple redirects
        if (!window._redirectingToLogin) {
          window._redirectingToLogin = true;

          try {
            // Clear session cache
            if (window._sessionCache) {
              window._sessionCache = null;
            }

            // Reset auth ready state
            resetAuthReady();

            // Force signout from Supabase
            await signOut();

            // Clear session storage
            sessionStorage.removeItem('activeConnectionId');

            // Small delay to ensure cleanup completes
            setTimeout(() => {
              // Redirect to login with current location
              const returnPath = window.location.pathname;
              window.location.href = `/login?returnTo=${encodeURIComponent(returnPath)}`;
            }, 100);

          } catch (e) {
            // Still redirect to login as fallback
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
        }

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Enhanced API request function with caching, abort control, and error handling
 * @param {Object} options - Request options
 * @returns {Promise} Promise resolving to the response data
 */
const enhancedRequest = async (options) => {
  const {
    method = 'GET',
    url,
    data,
    params,
    cacheKey,
    cacheTTL = 5 * 60 * 1000, // 5 minutes default
    forceFresh = false,
    requestId,
    abortSignal,
    onUploadProgress,
    onDownloadProgress,
    timeout = 60000 // 60 seconds timeout
  } = options;

  // Check cache first if this is a GET request and caching is enabled
  if (method === 'GET' && cacheKey && !forceFresh) {
    const cachedData = getCacheItem(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  }

  // Dedup via shared cross-library map (utils/requestDedup.js). Mutations
  // pass dedupKey === null and bypass dedup entirely.
  const dedupKey = buildDedupKey(method, url, params);

  return dedupedRequest(dedupKey, async () => {
    // Setup abort controller if needed
    const controller = requestId ? getRequestAbortController(requestId) : new AbortController();
    // Ensure controller is valid and has abort method
    const signal = abortSignal || (controller && typeof controller.abort === 'function' ?
                                 controller.signal : undefined);

    // Track whether the abort came from OUR timeout (a failure) vs from the
    // caller / unmount (legitimate cancellation). Without this distinction
    // we cannot tell whether to silently return null (user moved on) or throw
    // a real error (request timed out and the UI should treat it as such).
    // Conflating the two caused EnhancedHealthGauge to render 0% on a timed-out
    // /validations/summary because the silent null was treated as success.
    let timedOut = false;

    // Set a timeout
    let timeoutId;
    if (typeof controller?.abort === 'function') {
      timeoutId = setTimeout(() => {
        try {
          timedOut = true;
          controller.abort();
        } catch (e) {
          // Silent abort error
        }
      }, timeout);
    }

    try {
      const requestConfig = {
        method,
        url,
        data,
        params,
        signal,
        onUploadProgress,
        onDownloadProgress,
        timeout // Add explicit timeout to axios request
      };

      const response = await apiClient(requestConfig);

      // Cache the response if this is a GET request and caching is enabled
      if (method === 'GET' && cacheKey) {
        setCacheItem(cacheKey, response.data, { ttl: cacheTTL });
      }

      // Clear the abort controller
      if (requestId) {
        requestCompleted(requestId);
      }

      return response.data;
    } catch (error) {
      // Distinguish a timeout-triggered abort from a legitimate user/component
      // cancellation. A timeout is a real failure — React Query needs to see
      // an error so isError flips and consumers can render an error state
      // instead of treating "no data" as "score = 0". A user-initiated cancel
      // (e.g. component unmounted, user navigated) should still return null.
      if (isCancelledRequest(error)) {
        if (timedOut) {
          if (requestId) {
            requestCompleted(requestId);
          }
          const timeoutError = new Error(`Request timeout after ${timeout}ms: ${url}`);
          timeoutError.timeout = true;
          throw timeoutError;
        }
        return null;
      }

      // Clear the abort controller
      if (requestId) {
        requestCompleted(requestId);
      }

      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  });
};

// Enhanced Connections API
export const connectionsAPI = {
  getAll: (options = {}) => {
    const { forceFresh = false, requestId = 'connections.getAll' } = options;

    // Check if we already have cached data and we're not forcing a fresh fetch
    const cachedData = getCacheItem('connections.list');
    if (cachedData && !forceFresh) {
      // Return the cached data immediately
      return Promise.resolve(cachedData);
    }

    return enhancedRequest({
      url: '/connections',
      cacheKey: 'connections.list',
      requestId,
      forceFresh
    }).then(response => {
      // Handle different response formats
      if (response && response.data && response.data.connections) {
        return response; // Already in correct format
      } else if (response && response.connections) {
        // Wrap in data property for consistency
        return { data: { connections: response.connections } };
      } else if (Array.isArray(response)) {
        // Response is array, wrap in expected format
        return { data: { connections: response } };
      } else {
        // Fallback for unexpected response format
        return { data: { connections: [] } };
      }
    }).catch(error => {
      // Handle canceled requests
      if (error && error.cancelled) {
        throw error; // Just re-throw cancelled errors
      }
      // Return valid but empty response
      return { data: { connections: [] } };
    });
  },

  getById: (id, options = {}) => {
    const { forceFresh = false, requestId = `connections.get.${id}` } = options;
    return enhancedRequest({
      url: `/connections/${id}`,
      cacheKey: `connections.${id}`,
      requestId,
      forceFresh
    });
  },

  create: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections',
      data,
      requestId: 'connections.create'
    }).then(response => {
      // Invalidate connections list cache on create
      clearCacheItem('connections.list');
      // Broadcast a connection-created event so ConnectionContext (and
      // anyone else interested) can refresh without the caller having
      // to plumb a refresh callback through. Solves the recurring
      // "connection doesn't show on dashboard until refresh" bug —
      // OnboardingConnectionPage creates the connection directly via
      // this API (not through ConnectionContext.createConnection), so
      // the context never knew the list was stale. Reported 10+ times
      // in 2026-05-11/12 testing. Event-driven design is race-free
      // (no debounce, no async-await timing).
      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('sparvi:connection-created', {
            detail: { connection: response?.connection || response },
          }));
        } catch {
          // CustomEvent might fail in very old browsers; the cache
          // invalidation above is the fallback path.
        }
      }
      return response;
    });
  },

  update: (id, data) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/connections/${id}`,
      data,
      requestId: `connections.update.${id}`
    }).then(response => {
      // Invalidate affected cache items
      clearCacheItem(`connections.${id}`);
      clearCacheItem('connections.list');
      return response;
    });
  },

  delete: (id) => {
    return enhancedRequest({
      method: 'DELETE',
      url: `/connections/${id}`,
      requestId: `connections.delete.${id}`
    }).then(response => {
      // Invalidate affected cache items
      clearCacheItem(`connections.${id}`);
      clearCacheItem('connections.list');
      return response;
    });
  },

  test: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections/test',
      data,
      requestId: 'connections.test'
    });
  },

  setDefault: (id) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/connections/${id}/default`,
      requestId: `connections.setDefault.${id}`
    }).then(response => {
      // Invalidate connections list cache
      clearCacheItem('connections.list');
      return response;
    });
  },

  getConnectionDashboard: (connectionId, options = {}) => {
    const { forceFresh = false } = options;

    return enhancedRequest({
      method: 'POST',
      url: '/batch',
      data: {
        requests: [
          { id: 'connection', path: `/connections/${connectionId}` },
          { id: 'tables', path: `/connections/${connectionId}/tables` },
          { id: 'metadata', path: `/connections/${connectionId}/metadata/status` }
        ]
      },
      cacheKey: `connections.dashboard.${connectionId}`,
      cacheTTL: 2 * 60 * 1000,
      requestId: `connections.dashboard.${connectionId}`,
      forceFresh
    });
  },

  // OAuth Authentication Methods
  getSnowflakeAuthUrl: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/auth/snowflake/authorize',
      data,
      requestId: 'auth.snowflake.authorize'
    });
  },

  refreshOAuthToken: (connectionId) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/oauth/refresh/${connectionId}`,
      requestId: `connections.oauth.refresh.${connectionId}`
    }).then(response => {
      // Invalidate connection cache to force refresh with new token
      clearCacheItem(`connections.${connectionId}`);
      clearCacheItem('connections.list');
      return response;
    });
  },

  testOAuthConnection: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections/oauth/test',
      data,
      requestId: 'connections.oauth.test'
    });
  },

  getOAuthConnectionStatus: (connectionId) => {
    return enhancedRequest({
      url: `/connections/oauth/status/${connectionId}`,
      requestId: `connections.oauth.status.${connectionId}`
    });
  },

  // Key Pair Authentication Methods
  generateKeyPair: () => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections/snowflake/generate-keypair',
      requestId: 'connections.keypair.generate'
    });
  },

  testKeyPairConnection: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections/snowflake/test-keypair',
      data,
      requestId: 'connections.keypair.test'
    });
  },

  createKeyPairConnection: (data) => {
    return enhancedRequest({
      method: 'POST',
      url: '/connections/snowflake/create-keypair',
      data,
      requestId: 'connections.keypair.create'
    }).then(response => {
      // Invalidate connections list cache on create
      clearCacheItem('connections.list');
      return response;
    });
  },

  getOnboardingData: (connectionId) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/onboarding`,
      requestId: `connections.onboarding.${connectionId}`
    });
  },

  // Save the selected onboarding validation recommendations as real
  // validation_rules and execute them one-time. Replaces the Math.random
  // simulation that previously populated the onboarding results page.
  saveAndRunOnboardingValidations: ({ connectionId, validations }) => {
    return enhancedRequest({
      method: 'POST',
      url: '/onboarding/save-and-run-validations',
      data: { connection_id: connectionId, validations },
      requestId: `onboarding.save-and-run.${connectionId}`,
      // Override the 60s default. This endpoint saves rules and runs
      // each one against the warehouse sequentially — each BigQuery
      // query carries ~1-3s of startup overhead, so 9-15 rules across
      // a few tables can legitimately take 60-120s. Backend logs from
      // 2026-05-12 caught the client timing out at 60s while the
      // backend successfully completed at 84s, triggering a retry
      // that hit duplicate-rule errors. The latency optimization is
      // tracked separately in ROADMAP.md MVP Stability Checklist;
      // until then, a generous client timeout keeps the UX honest.
      timeout: 300000, // 5 minutes
    });
  },

  triggerLineageDiscovery: (connectionId) => {
    return enhancedRequest({
      url: `/lineage/discovery/${connectionId}`,
      method: 'POST',
      requestId: `lineage.discovery.${connectionId}`
    });
  },

  discoverTables: (connectionId) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/discover-tables`,
      requestId: `connections.discover.${connectionId}`,
      timeout: 120000 // 2 minutes timeout for table discovery
    });
  },

  // Progressive discovery methods
  discoverDatabases: (connectionId) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/discover-databases`,
      requestId: `connections.discover-db.${connectionId}`,
      timeout: 30000 // 30 seconds - lightweight operation
    });
  },

  discoverSchemas: (connectionId, selectedDatabases) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/discover-schemas`,
      method: 'POST',
      data: { databases: selectedDatabases },
      requestId: `connections.discover-schemas.${connectionId}`,
      timeout: 60000 // 1 minute timeout
    });
  },

  discoverTablesForSchemas: (connectionId, selectedSchemas) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/discover-tables`,
      method: 'POST',
      data: { schemas: selectedSchemas },
      requestId: `connections.discover-tables.${connectionId}`,
      timeout: 120000 // 2 minutes timeout
    });
  },

  // Tier Configuration Methods
  getTierConfig: (connectionId) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/tier-config`,
      requestId: `connections.tier-config.${connectionId}`
    });
  },

  updateTierConfig: (connectionId, tierConfig) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/connections/${connectionId}/tier-config`,
      data: { tier_config: tierConfig },
      requestId: `connections.tier-config.update.${connectionId}`
    });
  }
};

// Schema Changes API (Tier 3 functionality)
// Note: getTables, getColumns, getStatistics removed - use metadataAPI instead
export const schemaAPI = {
  detectChanges: (connectionId, options = {}) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/schema/detect-changes`,
      requestId: `schema.detectChanges.${connectionId}`,
      headers: {
        'Content-Type': 'application/json'
      },
      data: options || {}  // Make sure we send an empty object if no options
    }).then(response => {
      // Clear schema cache if changes were detected
      if (response.changes_detected > 0) {
        clearCacheItem(`schema.tables.${connectionId}`);
      }
      return response;
    });
  },

  getChanges: (connectionId, options = {}) => {
    // Change default to include acknowledged changes
    const { acknowledged = 'all', since = null } = options;

    const params = {};
    // Only add acknowledged param if it's not 'all'
    if (acknowledged !== 'all') {
      params.acknowledged = acknowledged;
    }
    if (since) params.since = since;

    return enhancedRequest({
      url: `/connections/${connectionId}/changes`,
      params,
      requestId: `schema.changes.${connectionId}`,
      // Don't cache changes - they should always be fresh
      cacheKey: null
    });
  },

  acknowledgeChanges: (connectionId, tableName) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/changes/acknowledge`,
      data: { table_name: tableName },
      requestId: `schema.acknowledge.${connectionId}.${tableName}`
    });
  }
};

// Enhanced Profiling API
export const profilingAPI = {
  getProfile: (connectionId, tableName, options = {}) => {
    const { forceFresh = false, requestId = `profile.${connectionId}.${tableName}` } = options;
    return enhancedRequest({
      url: '/profile',
      params: { connection_id: connectionId, table: tableName },
      cacheKey: `profile.${connectionId}.${tableName}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  getHistory: (tableName, limit = 10, options = {}) => {
    const { forceFresh = false, requestId = `profile.history.${tableName}` } = options;
    return enhancedRequest({
      url: '/profile-history',
      params: { table: tableName, limit },
      cacheKey: `profile.history.${tableName}.limit${limit}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  getTrends: (connectionId, tableName, days = 30, options = {}) => {
    const { forceFresh = false, requestId = `profile.trends.${connectionId}.${tableName}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/tables/${encodeURIComponent(tableName)}/trends`,
      params: { days },
      cacheKey: `profile.trends.${connectionId}.${tableName}.days${days}`,
      cacheTTL: 30 * 60 * 1000, // 30 minutes since trends don't change frequently
      requestId,
      forceFresh
    });
  }
};

// Enhanced Validations API
export const validationsAPI = {
  getUnifiedValidations: (params) => {
    return enhancedRequest({
      url: '/validations/unified',
      params: Object.fromEntries(params),
      cacheKey: `validations.unified.${params.get('table')}.${params.get('source_type') || 'all'}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `validations.unified.${params.get('table')}`
    }).then(response => {
      // Normalize the response structure
      if (response?.tests) {
        return response.tests;
      } else if (response?.data?.tests) {
        return response.data.tests;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];
    });
  },

  getRules: (tableName, options = {}) => {
    const {
      forceFresh = false,
      requestId = `validations.rules.${tableName}`,
      connectionId,
      includeInactive = false
    } = options;

    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    return enhancedRequest({
      url: '/validations',
      params: {
        table: tableName,
        connection_id: connectionId,
        include_inactive: includeInactive
      },
      cacheKey: `validations.rules.${tableName}${includeInactive ? '.all' : ''}`,
      cacheTTL: 10 * 60 * 1000,
      requestId,
      forceFresh
    }).then(response => {
      // Normalize the response structure
      if (response && response.data && response.data.rules) {
        return { data: { rules: response.data.rules } };
      } else if (response && response.rules) {
        return { data: { rules: response.rules } };
      } else if (Array.isArray(response)) {
        return { data: { rules: response } };
      }
      return { data: { rules: [] } };
    });
  },

  createRule: (tableName, rule, connectionId) => {
    return enhancedRequest({
      method: 'POST',
      url: '/validations',
      params: { table: tableName, connection_id: connectionId },
      data: rule,
      requestId: `validations.create.${tableName}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  updateRule: (ruleId, tableName, rule, connectionId) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/validations/${ruleId}`,
      params: { table: tableName, connection_id: connectionId },
      data: rule,
      requestId: `validations.update.${tableName}.${ruleId}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  deleteRule: (tableName, ruleName, connectionId) => {
    return enhancedRequest({
      method: 'DELETE',
      url: '/validations',
      params: { table: tableName, rule_name: ruleName, connection_id: connectionId },
      requestId: `validations.delete.${tableName}.${ruleName}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  deactivateRule: (tableName, ruleName, connectionId) => {
    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    return enhancedRequest({
      method: 'PUT',
      url: '/validations/deactivate',
      params: {
        table: tableName,
        rule_name: ruleName,
        connection_id: connectionId
      },
      requestId: `validations.deactivate.${tableName}.${ruleName}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  activateRule: (tableName, ruleName, connectionId) => {
    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    return enhancedRequest({
      method: 'PUT',
      url: '/validations/activate',
      params: {
        table: tableName,
        rule_name: ruleName,
        connection_id: connectionId
      },
      requestId: `validations.activate.${tableName}.${ruleName}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  getSummary: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `validations.summary.${connectionId}` } = options;

    const isProduction = process.env.NODE_ENV === 'production';
    const cacheTTL = isProduction ? 30 * 60 * 1000 : 5 * 60 * 1000;

    return enhancedRequest({
      url: '/validations/summary',
      params: { connection_id: connectionId },
      cacheKey: `validations.summary.${connectionId}`,
      cacheTTL: cacheTTL,
      requestId,
      forceFresh
    });
  },

  runValidations: (connectionId, tableName, connectionString, options = {}) => {
    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    const {
      timeout = 600000,
      requestId = `validations.run.${connectionId}.${tableName}`
    } = options;

    return enhancedRequest({
      method: 'POST',
      url: '/run-validations',
      data: {
        connection_id: connectionId,
        table: tableName,
        connection_string: connectionString
      },
      timeout,
      requestId
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);

      if (response && typeof response === 'object') {
        if (response.results && Array.isArray(response.results)) {
          return response;
        } else if (Array.isArray(response)) {
          return { results: response };
        } else if (response.data) {
          if (Array.isArray(response.data)) {
            return { results: response.data };
          } else if (response.data.results) {
            return { results: response.data.results };
          }
        }
      }
      return { results: Array.isArray(response) ? response : [response] };
    });
  },

  runSingleValidation: (data, options = {}) => {
    const { connection_id: connectionId, table: tableName } = data;

    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    if (!tableName) {
      return Promise.reject(new Error('tableName is required'));
    }

    const {
      timeout = 60000,
      requestId = `validations.runSingle.${connectionId}.${tableName}.${data.rule_id || data.rule_name}`
    } = options;

    return enhancedRequest({
      method: 'POST',
      url: '/run-validations/single',
      data,
      timeout,
      requestId
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      return response;
    });
  },

  getValidationHistory: (tableName, connectionId, options = {}) => {
    const {
      limit = 30,
      forceFresh = false,
      requestId = `validations.history.${tableName}`
    } = options;

    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    return enhancedRequest({
      url: '/validation-history',
      params: {
        table: tableName,
        connection_id: connectionId,
        limit
      },
      cacheKey: `validations.history.${tableName}.${connectionId}.${limit}`,
      cacheTTL: 30 * 60 * 1000,
      requestId,
      forceFresh
    }).catch(() => {
      // Return empty history if we fail
      return { history: [] };
    });
  },

  getValidationTrends: (connectionId, tableName, options = {}) => {
    const {
      days = 30,
      forceFresh = false,
      requestId = `validations.trends.${connectionId}.${tableName}`
    } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/validations/${tableName}/trends`,
      params: { days },
      cacheKey: `validations.trends.${connectionId}.${tableName}.days${days}`,
      cacheTTL: 10 * 60 * 1000,
      requestId,
      forceFresh
    });
  },

  getLatestValidationResults: (connectionId, tableName, options = {}) => {
    const {
      forceFresh = false,
      requestId = `validations.latest.${connectionId}.${tableName}`
    } = options;

    if (!connectionId || !tableName) {
      return Promise.reject(new Error('Missing required parameters'));
    }

    return enhancedRequest({
      url: `/validations/latest/${connectionId}/${encodeURIComponent(tableName)}`,
      cacheKey: `validations.latest.${connectionId}.${tableName}`,
      cacheTTL: 5 * 60 * 1000,
      requestId,
      forceFresh
    });
  },

  generateDefaultValidations: (connectionId, tableName, connectionString) => {
    if (!connectionId) {
      return Promise.reject(new Error('connectionId is required'));
    }

    return enhancedRequest({
      method: 'POST',
      url: '/generate-default-validations',
      data: {
        connection_id: connectionId,
        table: tableName,
        connection_string: connectionString
      },
      requestId: `validations.generate.${connectionId}.${tableName}`
    }).then(response => {
      clearCacheItem(`validations.rules.${tableName}`);
      clearCacheItem(`validations.summary.${connectionId}`);
      clearCacheItem(`table.dashboard.${connectionId}.${tableName}`);

      return enhancedRequest({
        url: '/validations',
        params: {
          table: tableName,
          connection_id: connectionId
        },
        requestId: `validations.get.${tableName}.${connectionId}`,
        forceFresh: true
      }).then(rulesResponse => {
        return {
          generation: response,
          rules: rulesResponse
        };
      }).catch(() => {
        return response;
      });
    });
  }
};

// Enhanced Metadata API
export const metadataAPI = {
  getMetadata: (connectionId, type = 'tables', options = {}) => {
    const { forceFresh = false, requestId = `metadata.${connectionId}.${type}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/metadata`,
      params: { type },
      cacheKey: `metadata.${connectionId}.${type}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  collectMetadata: (connectionId, options = {}) => {
    const requestBody = { ...options };
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/metadata/collect`,
      data: requestBody,
      requestId: `metadata.collect.${connectionId}`
    }).then(response => {
      // Invalidate metadata caches for this connection
      clearCacheItem(`metadata.${connectionId}.tables`);
      clearCacheItem(`metadata.${connectionId}.columns`);
      clearCacheItem(`metadata.${connectionId}.statistics`);
      return response;
    });
  },

  refreshMetadata: (connectionId, metadataType, tableName, priority = 'high') => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/metadata/refresh`,
      data: {
        metadata_type: metadataType,
        table_name: tableName,
        priority: priority
      },
      requestId: `metadata.refresh.${connectionId}.${metadataType}`
    }).then(response => {
      // Invalidate affected metadata caches
      clearCacheItem(`metadata.${connectionId}.${metadataType}`);
      if (tableName) {
        clearCacheItem(`schema.columns.${connectionId}.${tableName}`);
        clearCacheItem(`schema.statistics.${connectionId}.${tableName}`);
      }
      return response;
    });
  },

  getMetadataStatus: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `metadata.status.${connectionId}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/status`,
      cacheKey: `metadata.status.${connectionId}`,
      cacheTTL: 2 * 60 * 1000, // 2 minutes (status changes frequently)
      requestId,
      forceFresh
    });
  },

  getTasks: (connectionId, limit = 10, options = {}) => {
    const { forceFresh = false, requestId = `metadata.tasks.${connectionId}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/tasks`,
      params: { limit },
      cacheKey: `metadata.tasks.${connectionId}.limit${limit}`,
      cacheTTL: 1 * 60 * 1000, // 1 minute (tasks change frequently)
      requestId,
      forceFresh
    });
  },

  scheduleTask: (connectionId, taskType, tableName, priority = 'medium') => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/metadata/tasks`,
      data: {
        task_type: taskType,
        table_name: tableName,
        priority
      },
      requestId: `metadata.schedule.${connectionId}.${taskType}`
    });
  },

  getTaskStatus: (connectionId, taskId) => {
    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/tasks/${taskId}`,
      requestId: `metadata.taskStatus.${connectionId}.${taskId}`
      // Don't cache task status - it changes frequently
    });
  },

  // ========================================================================
  // TIER-AWARE METADATA API (Unified Metadata Service)
  // New methods that leverage the tiered metadata system
  // ========================================================================

  /**
   * Get table metadata with tier awareness and filtering
   * Uses UnifiedMetadataService backend, reads from table_metadata_cache
   * @param {string} connectionId - Database connection ID
   * @param {Object} filters - Optional filters (database, schema, table_name, include_inactive, show_all_cached)
   * @param {Object} options - Request options (forceFresh, requestId)
   * @returns {Promise} Promise resolving to table metadata array
   */
  getTableMetadataTiered: (connectionId, filters = {}, options = {}) => {
    const { database = null, schema = null, table_name = null, include_inactive = false, show_all_cached = false } = filters;
    const { forceFresh = false, requestId = `metadata.tiered.tables.${connectionId}` } = options;

    const params = {};
    if (database) params.database = database;
    if (schema) params.schema = schema;
    if (table_name) params.table_name = table_name;
    if (include_inactive) params.include_inactive = 'true';
    if (show_all_cached) params.show_all_cached = 'true';

    const cacheKey = `metadata.tiered.${connectionId}.${database || 'all'}.${schema || 'all'}.${table_name || 'all'}.${show_all_cached ? 'allcached' : 'filtered'}`;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/tables`,
      params,
      cacheKey,
      cacheTTL: 10 * 60 * 1000, // 10 minutes (tiered data is fresher)
      requestId,
      forceFresh
    });
  },

  /**
   * Get ALL available schemas directly from the database
   * Queries database directly (SHOW SCHEMAS) rather than using cache
   * Returns schemas even if they have no cached tables
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to array of schema objects {database, schema, full_name}
   */
  getAvailableSchemas: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `metadata.schemas.available.${connectionId}` } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/schemas/available`,
      cacheKey: `metadata.schemas.available.${connectionId}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes (relatively stable)
      requestId,
      forceFresh
    });
  },

  /**
   * Get list of schemas for a connection
   * Uses UnifiedMetadataService backend
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to array of schema names
   */
  getSchemaList: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `metadata.schemas.${connectionId}` } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/schemas`,
      cacheKey: `metadata.schemas.${connectionId}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  /**
   * Get schema summary with aggregated metadata
   * Uses UnifiedMetadataService backend
   * @param {string} connectionId - Database connection ID
   * @param {string} schema - Schema name
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to schema summary object
   */
  getSchemaMetadata: (connectionId, schema, options = {}) => {
    const { forceFresh = false, requestId = `metadata.schema.${connectionId}.${schema}` } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/schemas/${schema}`,
      cacheKey: `metadata.schema.${connectionId}.${schema}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  /**
   * Get tables for a specific schema
   * Uses UnifiedMetadataService backend (convenience method)
   * @param {string} connectionId - Database connection ID
   * @param {string} schema - Schema name
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to table metadata array
   */
  getTablesBySchema: (connectionId, schema, options = {}) => {
    return metadataAPI.getTableMetadataTiered(
      connectionId,
      { schema },
      options
    );
  },

  /**
   * Get connection statistics (aggregated across all tables)
   * Uses UnifiedMetadataService backend
   * @param {string} connectionId - Database connection ID
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to connection statistics object
   */
  getConnectionStatistics: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `metadata.stats.${connectionId}` } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/statistics`,
      cacheKey: `metadata.stats.${connectionId}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  /**
   * Get column metadata for a specific table
   * Uses UnifiedMetadataService backend
   * @param {string} connectionId - Database connection ID
   * @param {string} tableName - Table name
   * @param {string} schema - Schema name (optional but recommended)
   * @param {Object} options - Request options
   * @returns {Promise} Promise resolving to column metadata array
   */
  getTableColumnsTiered: (connectionId, tableName, schema = null, options = {}) => {
    const { forceFresh = false, requestId = `metadata.columns.${connectionId}.${tableName}` } = options;

    const params = { table_name: tableName };
    if (schema) params.schema = schema;

    return enhancedRequest({
      url: `/connections/${connectionId}/metadata/columns`,
      params,
      cacheKey: `metadata.columns.${connectionId}.${schema || 'unknown'}.${tableName}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  // ========================================================================
  // TIERED METADATA COLLECTION API
  // ========================================================================

  /**
   * Get tier status and statistics for a connection
   * Shows Tier 1-5 execution status, last run times, and metrics
   */
  getTierStatus: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `tier.status.${connectionId}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/status`,
      cacheKey: `tier.status.${connectionId}`,
      cacheTTL: 30 * 1000, // 30 seconds (tier status changes frequently)
      requestId,
      forceFresh
    });
  },

  /**
   * Get tier job history for a connection
   * Returns list of recent tier collection jobs with status and results
   */
  getTierJobs: (connectionId, options = {}) => {
    const { limit = 20, tier = null, status = null, forceFresh = false } = options;
    const params = { limit };
    if (tier) params.tier = tier;
    if (status) params.status = status;

    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/jobs`,
      params,
      cacheKey: `tier.jobs.${connectionId}.${tier || 'all'}.${status || 'all'}.limit${limit}`,
      cacheTTL: 15 * 1000, // 15 seconds (job list changes frequently)
      requestId: `tier.jobs.${connectionId}`,
      forceFresh
    });
  },

  /**
   * Get detailed job information
   */
  getTierJob: (connectionId, jobId, options = {}) => {
    const { forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/jobs/${jobId}`,
      cacheKey: `tier.job.${jobId}`,
      cacheTTL: 10 * 1000, // 10 seconds
      requestId: `tier.job.${jobId}`,
      forceFresh
    });
  },

  /**
   * Trigger a specific tier collection manually
   * tierType: 'tier1_discovery', 'tier2_schema', 'tier3_changes', 'tier4_profiling', 'tier5_lineage', 'tier6_usage'
   */
  triggerTier: (connectionId, tierType, options = {}) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/tiered-metadata/trigger/${tierType}`,
      data: options,
      requestId: `tier.trigger.${connectionId}.${tierType}`
    }).then(response => {
      // Invalidate tier status and job caches
      clearCacheItem(`tier.status.${connectionId}`);
      clearCacheItem(`tier.jobs.${connectionId}.all.all.limit20`);
      // Also invalidate metadata caches since tier updates metadata
      clearCacheItem(`metadata.${connectionId}.tables`);
      clearCacheItem(`metadata.status.${connectionId}`);
      return response;
    });
  },

  /**
   * Trigger all tiers for a connection (full refresh)
   */
  triggerAllTiers: (connectionId, options = {}) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/tiered-metadata/trigger-all`,
      data: options,
      requestId: `tier.trigger.all.${connectionId}`
    }).then(response => {
      // Invalidate all tier and metadata caches
      clearCacheItem(`tier.status.${connectionId}`);
      clearCacheItem(`tier.jobs.${connectionId}.all.all.limit20`);
      clearCacheItem(`metadata.${connectionId}.tables`);
      clearCacheItem(`metadata.${connectionId}.columns`);
      clearCacheItem(`metadata.status.${connectionId}`);
      return response;
    });
  },

  /**
   * Get tier statistics for a connection
   * Returns aggregated statistics across all tiers
   */
  getTierStatistics: (connectionId, options = {}) => {
    const { days = 7, forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/stats`,
      params: { days },
      cacheKey: `tier.stats.${connectionId}.days${days}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes (stats are historical)
      requestId: `tier.stats.${connectionId}`,
      forceFresh
    });
  },

  /**
   * Get system-wide tier statistics (admin)
   * Returns statistics across all connections
   */
  getSystemTierStatistics: (options = {}) => {
    const { days = 7, forceFresh = false } = options;
    return enhancedRequest({
      url: `/tiered-metadata/system/stats`,
      params: { days },
      cacheKey: `tier.system.stats.days${days}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: 'tier.system.stats',
      forceFresh
    });
  },

  /**
   * Get Tier 4 profiling data for a specific table
   * Returns detailed column statistics (MIN, MAX, AVG, DISTINCT, NULL counts)
   */
  getTableProfiling: (connectionId, tableId, options = {}) => {
    const { forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/profiling/${tableId}`,
      cacheKey: `tier.profiling.table.${tableId}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes (profiling data doesn't change frequently)
      requestId: `tier.profiling.table.${tableId}`,
      forceFresh
    });
  },

  /**
   * Get profiling summary for all tables in a connection
   * Returns profiling coverage and summary statistics
   */
  getConnectionProfilingSummary: (connectionId, options = {}) => {
    const { schema = null, forceFresh = false } = options;
    const params = {};
    if (schema) params.schema = schema;

    return enhancedRequest({
      url: `/connections/${connectionId}/tiered-metadata/profiling`,
      params,
      cacheKey: `tier.profiling.summary.${connectionId}.${schema || 'all'}`,
      cacheTTL: 2 * 60 * 1000, // 2 minutes
      requestId: `tier.profiling.summary.${connectionId}`,
      forceFresh
    });
  }
};

// Enhanced Admin API
export const adminAPI = {
  getUsers: (options = {}) => {
    const { forceFresh = false, requestId = 'admin.users' } = options;
    return enhancedRequest({
      url: '/admin/users',
      cacheKey: 'admin.users',
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  },

  updateUser: (userId, userData) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/admin/users/${userId}`,
      data: userData,
      requestId: `admin.updateUser.${userId}`
    }).then(response => {
      // Invalidate users cache
      clearCacheItem('admin.users');
      return response;
    });
  },

  inviteUser: (userData) => {
    return enhancedRequest({
      method: 'POST',
      url: '/admin/users',
      data: userData,
      requestId: 'admin.inviteUser'
    }).then(response => {
      // Invalidate users cache
      clearCacheItem('admin.users');
      return response;
    });
  },

  removeUser: (userId) => {
    return enhancedRequest({
      method: 'DELETE',
      url: `/admin/users/${userId}`,
      requestId: `admin.removeUser.${userId}`
    }).then(response => {
      // Invalidate users cache
      clearCacheItem('admin.users');
      return response;
    });
  },

  getOrganization: (options = {}) => {
    const { forceFresh = false, requestId = 'admin.organization' } = options;
    return enhancedRequest({
      url: '/admin/organization',
      cacheKey: 'admin.organization',
      cacheTTL: 30 * 60 * 1000, // 30 minutes (organization details change rarely)
      requestId,
      forceFresh
    });
  },

  updateOrganization: (orgData) => {
    return enhancedRequest({
      method: 'PUT',
      url: '/admin/organization',
      data: orgData,
      requestId: 'admin.updateOrganization'
    }).then(response => {
      // Invalidate organization cache
      clearCacheItem('admin.organization');
      return response;
    });
  },

  getWorkerStats: (options = {}) => {
    const { requestId = 'admin.workerStats' } = options;
    return enhancedRequest({
      url: '/metadata/worker/stats',
      requestId
      // Don't cache worker stats - they change frequently
    });
  }
};

// Enhanced User Setup API
export const userAPI = {
  setupUser: (userData) => {
    return enhancedRequest({
      method: 'POST',
      url: '/setup-user',
      data: userData,
      requestId: 'user.setup'
    });
  },

  getProfile: (userId = null, options = {}) => {
    const { forceFresh = false, requestId = `user.profile.${userId || 'current'}` } = options;
    const url = userId ? `/users/${userId}/profile` : '/user/profile';
    
    return enhancedRequest({
      url,
      cacheKey: `user.profile.${userId || 'current'}`,
      cacheTTL: 10 * 60 * 1000, // 10 minutes
      requestId,
      forceFresh
    });
  },

  updateProfile: (profileData, userId = null, options = {}) => {
    const { requestId = `user.profile.update.${userId || 'current'}` } = options;
    const url = userId ? `/users/${userId}/profile` : '/user/profile';
    
    return enhancedRequest({
      method: 'PATCH',
      url,
      data: profileData,
      requestId,
      // Clear cache after update
      onSuccess: () => {
        clearCacheItem(`user.profile.${userId || 'current'}`);
      }
    });
  },

  markFirstLoginComplete: (userId = null, options = {}) => {
    return userAPI.updateProfile(
      { first_login_completed: true, first_login_completed_at: new Date().toISOString() },
      userId,
      options
    );
  },

  // Onboarding API methods
  startOnboarding: (onboardingType = 'app_orientation', options = {}) => {
    const { requestId = 'user.onboarding.start' } = options;
    
    return enhancedRequest({
      method: 'POST',
      url: '/user/onboarding/start',
      data: { onboarding_type: onboardingType },
      requestId
    });
  },

  updateOnboardingProgress: (progressData, options = {}) => {
    const { requestId = 'user.onboarding.progress' } = options;
    
    return enhancedRequest({
      method: 'PATCH',
      url: '/user/onboarding/progress',
      data: progressData,
      requestId
    });
  },

  completeOnboarding: (completionType = 'welcome_flow', durationMinutes = null, options = {}) => {
    const { requestId = 'user.onboarding.complete' } = options;
    
    const data = { completion_type: completionType };
    if (durationMinutes !== null) {
      data.duration_minutes = durationMinutes;
    }
    
    return enhancedRequest({
      method: 'POST',
      url: '/user/onboarding/complete',
      data,
      requestId
    });
  },

  getOnboardingStatus: (options = {}) => {
    const { forceFresh = false, requestId = 'user.onboarding.status' } = options;

    return enhancedRequest({
      url: '/user/onboarding/status',
      cacheKey: 'user.onboarding.status',
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  }
};

// Enhanced Analytics API
export const analyticsAPI = {
  getChangeFrequency: (connectionId, objectType, objectName, days = 30, options = {}) => {
    const { forceFresh = false, requestId = `analytics.changeFrequency.${connectionId}.${objectName}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/change-frequency`,
      params: { object_type: objectType, object_name: objectName, days },
      cacheKey: `analytics.changeFrequency.${connectionId}.${objectType}.${objectName}.days${days}`,
      cacheTTL: 1 * 60 * 60 * 1000, // 1 hour (analytics data changes slowly)
      requestId,
      forceFresh
    });
  },

  getRefreshSuggestion: (connectionId, objectType, objectName, currentInterval = 24, options = {}) => {
    const { forceFresh = false, requestId = `analytics.refreshSuggestion.${connectionId}.${objectName}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/refresh-suggestion`,
      params: {
        object_type: objectType,
        object_name: objectName,
        current_interval: currentInterval
      },
      cacheKey: `analytics.refreshSuggestion.${connectionId}.${objectType}.${objectName}.interval${currentInterval}`,
      cacheTTL: 12 * 60 * 60 * 1000, // 12 hours (suggestions change very slowly)
      requestId,
      forceFresh
    });
  },

  getHighImpactObjects: (connectionId, limit = 10, options = {}) => {
    const { forceFresh = false, requestId = `analytics.highImpact.${connectionId}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/high-impact`,
      params: { limit },
      cacheKey: `analytics.highImpact.${connectionId}.limit${limit}`,
      cacheTTL: 6 * 60 * 60 * 1000, // 6 hours (high impact objects change slowly)
      requestId,
      forceFresh
    });
  },

  getDashboard: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `analytics.dashboard.${connectionId}` } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/dashboard`,
      cacheKey: `analytics.dashboard.${connectionId}`,
      cacheTTL: 1 * 60 * 60 * 1000, // 1 hour
      requestId,
      forceFresh
    });
  },

  // New methods for enhanced analytics

  getHistoricalMetrics: (connectionId, options = {}) => {
    const {
      metric_name, table_name, column_name, days = 30,
      limit = 100, group_by_date = false, forceFresh = false
    } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/historical-metrics`,
      params: {
        metric_name, table_name, column_name,
        days, limit, group_by_date
      },
      cacheKey: `analytics.historicalMetrics.${connectionId}.${metric_name || 'all'}.${table_name || 'all'}.${days}`,
      cacheTTL: 1 * 60 * 60 * 1000, // 1 hour
      requestId: `analytics.historicalMetrics.${connectionId}`,
      forceFresh
    });
  },

  trackMetrics: (connectionId, metrics) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/analytics/track-metrics`,
      data: { metrics },
      requestId: `analytics.trackMetrics.${connectionId}`
    });
  },

  // New analytics v2 endpoints using proven patterns
  getAnalyticsOverview: (connectionId, options = {}) => {
    const { days = 30, forceFresh = false } = options;
    
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/overview`,
      params: { days },
      cacheKey: `analytics.overview.${connectionId}.${days}`,
      cacheTTL: 2 * 60 * 1000, // 2 minutes for fresh data
      requestId: `analytics.overview.${connectionId}`,
      forceFresh
    });
  },
  
  getTableAnalytics: (connectionId, tableName, options = {}) => {
    const { days = 30, forceFresh = false } = options;
    
    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/table/${tableName}`,
      params: { days },
      cacheKey: `analytics.table.${connectionId}.${tableName}.${days}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `analytics.table.${connectionId}.${tableName}`,
      forceFresh
    });
  },
  
  // Legacy endpoint (keep for backward compatibility)
  getDashboardMetrics: (connectionId, options = {}) => {
    const { days = 30, limit = 100, forceFresh = false } = options;

    return enhancedRequest({
      url: `/connections/${connectionId}/analytics/dashboard/metrics`,
      params: { days, limit },
      cacheKey: `analytics.dashboardMetrics.${connectionId}.${days}`,
      cacheTTL: 1 * 60 * 60 * 1000, // 1 hour
      requestId: `analytics.dashboardMetrics.${connectionId}`,
      forceFresh
    });
  }
};

// Enhanced Anomaly API
export const anomalyAPI = {
  // Get anomaly configurations for a connection
  getConfigs: (connectionId, options = {}) => {
    const { table_name, metric_name, forceFresh = false } = options;
    const params = {};

    if (table_name) params.table_name = table_name;
    if (metric_name) params.metric_name = metric_name;

    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies/configs`,
      params,
      cacheKey: `anomaly.configs.${connectionId}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `anomaly.configs.${connectionId}`,
      forceFresh
    });
  },

  // Get a specific configuration
  getConfig: (connectionId, configId, options = {}) => {
    const { forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies/configs/${configId}`,
      cacheKey: `anomaly.config.${connectionId}.${configId}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `anomaly.config.${connectionId}.${configId}`,
      forceFresh
    });
  },

  // Create a new configuration
  createConfig: (connectionId, configData) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/anomalies/configs`,
      data: configData,
      requestId: `anomaly.createConfig.${connectionId}`
    }).then(response => {
      // Invalidate configs cache
      clearCacheItem(`anomaly.configs.${connectionId}`);
      return response;
    });
  },

  // Update a configuration
  updateConfig: (connectionId, configId, configData) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/connections/${connectionId}/anomalies/configs/${configId}`,
      data: configData,
      requestId: `anomaly.updateConfig.${connectionId}.${configId}`
    }).then(response => {
      // Invalidate affected cache items
      clearCacheItem(`anomaly.config.${connectionId}.${configId}`);
      clearCacheItem(`anomaly.configs.${connectionId}`);
      return response;
    });
  },

  // Delete a configuration
  deleteConfig: (connectionId, configId) => {
    return enhancedRequest({
      method: 'DELETE',
      url: `/connections/${connectionId}/anomalies/configs/${configId}`,
      requestId: `anomaly.deleteConfig.${connectionId}.${configId}`
    }).then(response => {
      // Invalidate affected cache items
      clearCacheItem(`anomaly.config.${connectionId}.${configId}`);
      clearCacheItem(`anomaly.configs.${connectionId}`);
      return response;
    });
  },

  // Get anomalies for a connection
  getAnomalies: (connectionId, options = {}) => {
    const {
      table_name,
      status,
      days = 30,
      limit = 100,
      forceFresh = false
    } = options;

    const params = { days, limit };
    if (table_name) params.table_name = table_name;
    if (status) params.status = status;

    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies`,
      params,
      cacheKey: `anomaly.list.${connectionId}.${JSON.stringify(params)}`,
      cacheTTL: 2 * 60 * 1000, // 2 minutes (anomalies change frequently)
      requestId: `anomaly.list.${connectionId}`,
      forceFresh
    });
  },

  // Get a specific anomaly
  getAnomaly: (connectionId, anomalyId, options = {}) => {
    const { forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies/${anomalyId}`,
      cacheKey: `anomaly.detail.${connectionId}.${anomalyId}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `anomaly.detail.${connectionId}.${anomalyId}`,
      forceFresh
    });
  },

  // Update anomaly status
  updateAnomalyStatus: (connectionId, anomalyId, status, resolutionNote = null) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/connections/${connectionId}/anomalies/${anomalyId}/status`,
      data: {
        status,
        resolution_note: resolutionNote
      },
      requestId: `anomaly.updateStatus.${connectionId}.${anomalyId}`
    }).then(response => {
      // Invalidate affected cache items
      clearCacheItem(`anomaly.detail.${connectionId}.${anomalyId}`);
      clearCacheItem(`anomaly.list.${connectionId}`);
      clearCacheItem(`anomaly.summary.${connectionId}`);
      clearCacheItem(`anomaly.dashboard.${connectionId}`);
      return response;
    });
  },

  // Run anomaly detection manually
  runDetection: (connectionId, options = {}) => {
    return enhancedRequest({
      method: 'POST',
      url: `/connections/${connectionId}/anomalies/detect`,
      data: options,
      requestId: `anomaly.detect.${connectionId}`
    }).then(response => {
      // Invalidate anomaly-related caches after detection
      clearCacheItem(`anomaly.list.${connectionId}`);
      clearCacheItem(`anomaly.summary.${connectionId}`);
      clearCacheItem(`anomaly.dashboard.${connectionId}`);
      return response;
    });
  },

  // Get anomaly summary
  getSummary: (connectionId, options = {}) => {
    const { days = 30, forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies/summary`,
      params: { days },
      cacheKey: `anomaly.summary.${connectionId}.${days}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `anomaly.summary.${connectionId}`,
      forceFresh
    });
  },

  // Get dashboard data
  getDashboardData: (connectionId, options = {}) => {
    const { days = 30, forceFresh = false } = options;
    return enhancedRequest({
      url: `/connections/${connectionId}/anomalies/dashboard`,
      params: { days },
      cacheKey: `anomaly.dashboard.${connectionId}.${days}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId: `anomaly.dashboard.${connectionId}`,
      forceFresh
    });
  }
};

// Enhanced Automation API
export const automationAPI = {
  // Global Configuration
  getGlobalConfig: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.globalConfig.get' } = options;
    return enhancedRequest({
      url: '/automation/global-config',
      cacheKey: 'automation.globalConfig',
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  },

  updateGlobalConfig: (configData) => {
    return enhancedRequest({
      method: 'PUT',
      url: '/automation/global-config',
      data: configData,
      requestId: 'automation.globalConfig.update'
    }).then(response => {
      // Clear global config cache
      clearCacheItem('automation.globalConfig');
      return response;
    });
  },

  toggleGlobalAutomation: (enabled) => {
    return enhancedRequest({
      method: 'POST',
      url: '/automation/global-toggle',
      data: { enabled },
      requestId: 'automation.globalToggle'
    }).then(response => {
      // Clear global config cache
      clearCacheItem('automation.globalConfig');
      return response;
    });
  },

  // Enhanced status with next run times
  getEnhancedStatus: (connectionId = null, options = {}) => {
    const { forceFresh = false } = options;

    const params = {};
    if (connectionId) params.connection_id = connectionId;

    const requestId = connectionId
      ? `automation.enhancedStatus.${connectionId}`
      : 'automation.enhancedStatus.all';

    const cacheKey = connectionId
      ? `automation.enhancedStatus.${connectionId}`
      : 'automation.enhancedStatus.all';

    return enhancedRequest({
      url: '/automation/status-enhanced',
      params,
      cacheKey,
      cacheTTL: 1 * 60 * 1000, // 1 minute cache
      requestId,
      forceFresh
    });
  },

  // Connection Configuration
  getConnectionConfigs: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.connectionConfigs.getAll' } = options;
    return enhancedRequest({
      url: '/automation/connection-configs',
      cacheKey: 'automation.connectionConfigs',
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  },

  getConnectionConfig: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `automation.connectionConfig.get.${connectionId}` } = options;
    return enhancedRequest({
      url: `/automation/connection-configs/${connectionId}`,
      cacheKey: `automation.connectionConfig.${connectionId}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  },

  updateConnectionConfig: (connectionId, configData) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/automation/connection-configs/${connectionId}`,
      data: configData,
      requestId: `automation.connectionConfig.update.${connectionId}`
    }).then(response => {
      // Clear affected cache items
      clearCacheItem(`automation.connectionConfig.${connectionId}`);
      clearCacheItem('automation.connectionConfigs');
      clearCacheItem(`automation.status.${connectionId}`);
      return response;
    });
  },

  // Table Configuration
  getTableConfig: (connectionId, tableName, options = {}) => {
    const { forceFresh = false, requestId = `automation.tableConfig.get.${connectionId}.${tableName}` } = options;
    return enhancedRequest({
      url: `/automation/table-configs/${connectionId}/${tableName}`,
      cacheKey: `automation.tableConfig.${connectionId}.${tableName}`,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      requestId,
      forceFresh
    });
  },

  updateTableConfig: (connectionId, tableName, configData) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/automation/table-configs/${connectionId}/${tableName}`,
      data: configData,
      requestId: `automation.tableConfig.update.${connectionId}.${tableName}`
    }).then(response => {
      // Clear affected cache items
      clearCacheItem(`automation.tableConfig.${connectionId}.${tableName}`);
      return response;
    });
  },

  // Status and Monitoring
  getAutomationStatus: (organizationId = null, connectionId = null, options = {}) => {
    const { forceFresh = false } = options;
    const endpoint = connectionId
      ? `/automation/status/${connectionId}`
      : '/automation/status';

    const requestId = connectionId
      ? `automation.status.connection.${connectionId}`
      : 'automation.status.global';

    const cacheKey = connectionId
      ? `automation.status.${connectionId}`
      : 'automation.status.global';

    return enhancedRequest({
      url: endpoint,
      cacheKey,
      cacheTTL: 1 * 60 * 1000, // 1 minute (status changes frequently)
      requestId,
      forceFresh
    });
  },

  getJobs: (options = {}) => {
    const {
      connectionId,
      status,
      limit = 50,
      forceFresh = false,
      requestId = 'automation.jobs.get'
    } = options;

    const params = {};
    if (connectionId) params.connection_id = connectionId;
    if (status) params.status = status;
    if (limit) params.limit = limit;

    return enhancedRequest({
      url: '/automation/jobs',
      params,
      cacheKey: `automation.jobs.${JSON.stringify(params)}`,
      cacheTTL: 30 * 1000, // 30 seconds (jobs change frequently)
      requestId,
      forceFresh
    });
  },

  // Control Operations
  toggleConnectionAutomation: (connectionId, enabled) => {
    return enhancedRequest({
      method: 'POST',
      url: `/automation/toggle/${connectionId}`,
      data: { enabled },
      requestId: `automation.toggle.${connectionId}`
    }).then(response => {
      // Clear affected cache items
      clearCacheItem(`automation.connectionConfig.${connectionId}`);
      clearCacheItem(`automation.status.${connectionId}`);
      clearCacheItem('automation.connectionConfigs');
      return response;
    });
  },

  triggerAutomation: (connectionId, automationType = null) => {
    return enhancedRequest({
      method: 'POST',
      url: `/automation/trigger/${connectionId}`,
      data: { automation_type: automationType },
      requestId: `automation.trigger.${connectionId}.${automationType || 'all'}`
    }).then(response => {
      // Clear job cache to reflect new job
      clearCacheItem('automation.jobs');
      clearCacheItem(`automation.status.${connectionId}`);
      return response;
    });
  },

  cancelJob: (jobId) => {
    return enhancedRequest({
      method: 'POST',
      url: `/automation/jobs/${jobId}/cancel`,
      requestId: `automation.cancelJob.${jobId}`
    }).then(response => {
      // Clear job cache
      clearCacheItem('automation.jobs');
      return response;
    });
  },

  // Dashboard
  getDashboard: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.dashboard' } = options;
    return enhancedRequest({
      url: '/automation/dashboard',
      cacheKey: 'automation.dashboard',
      cacheTTL: 2 * 60 * 1000, // 2 minutes
      requestId,
      forceFresh
    });
  },

  // Templates
  getTemplates: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.templates' } = options;
    return enhancedRequest({
      url: '/automation/templates',
      cacheKey: 'automation.templates',
      cacheTTL: 30 * 60 * 1000, // 30 minutes (templates don't change often)
      requestId,
      forceFresh
    });
  },

  // Bulk Operations
  bulkUpdateConfigs: (connectionIds, configData) => {
    return enhancedRequest({
      method: 'POST',
      url: '/automation/bulk-update',
      data: {
        connection_ids: connectionIds,
        config: configData
      },
      requestId: 'automation.bulkUpdate'
    }).then(response => {
      // Clear all connection config caches
      connectionIds.forEach(id => {
        clearCacheItem(`automation.connectionConfig.${id}`);
        clearCacheItem(`automation.status.${id}`);
      });
      clearCacheItem('automation.connectionConfigs');
      return response;
    });
  },

  // Admin Operations
  getSchedulerStatus: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.scheduler.status' } = options;
    return enhancedRequest({
      url: '/automation/scheduler/status',
      cacheKey: 'automation.scheduler.status',
      cacheTTL: 30 * 1000, // 30 seconds
      requestId,
      forceFresh
    });
  },

  getSchedule: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `automation.schedule.${connectionId}` } = options;
    return enhancedRequest({
      url: `/automation/schedules/${connectionId}`,
      cacheKey: `automation.schedule.${connectionId}`,
      cacheTTL: 2 * 60 * 1000, // 2 minutes - schedules don't change often
      requestId,
      forceFresh
    });
  },

  // Update schedule for a connection
  updateSchedule: (connectionId, scheduleConfig) => {
    return enhancedRequest({
      method: 'PUT',
      url: `/automation/schedules/${connectionId}`,
      data: { schedule_config: scheduleConfig },
      requestId: `automation.updateSchedule.${connectionId}`
    }).then(response => {
      // Clear affected cache items
      clearCacheItem(`automation.schedule.${connectionId}`);
      clearCacheItem(`automation.nextRuns.${connectionId}`);
      clearCacheItem('automation.allNextRuns');
      return response;
    });
  },

  // Get next run times for a specific connection
  getNextRunTimes: (connectionId, options = {}) => {
    const { forceFresh = false, requestId = `automation.nextRuns.${connectionId}` } = options;
    return enhancedRequest({
      url: `/automation/schedules/${connectionId}/next-runs`,
      cacheKey: `automation.nextRuns.${connectionId}`,
      cacheTTL: 1 * 60 * 1000,
      requestId,
      forceFresh
    }).catch(error => {
      return {
        connection_id: connectionId,
        next_runs: {},
        error: error.message
      };
    });
  },

  // Get next run times for all connections
  getAllNextRunTimes: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.allNextRuns' } = options;
    return enhancedRequest({
      url: '/automation/schedules/all',
      cacheKey: 'automation.allNextRuns',
      cacheTTL: 1 * 60 * 1000,
      requestId,
      forceFresh
    }).catch(error => {
      return {
        next_runs_by_connection: {},
        error: error.message
      };
    });
  },

  // Validate schedule configuration
  validateSchedule: (scheduleConfig) => {
    return enhancedRequest({
      method: 'POST',
      url: '/automation/schedules/validate',
      data: { schedule_config: scheduleConfig },
      requestId: 'automation.validateSchedule'
    });
  },

  // Get schedule templates
  getScheduleTemplates: (options = {}) => {
    const { forceFresh = false, requestId = 'automation.scheduleTemplates' } = options;
    return enhancedRequest({
      url: '/automation/schedules/templates',
      cacheKey: 'automation.scheduleTemplates',
      cacheTTL: 30 * 60 * 1000, // 30 minutes - templates rarely change
      requestId,
      forceFresh
    });
  },

  // Disable all automation for a connection
  disableAllAutomation: (connectionId) => {
    return enhancedRequest({
      method: 'POST',
      url: `/automation/schedules/${connectionId}/disable`,
      requestId: `automation.disableAll.${connectionId}`
    }).then(response => {
      // Clear affected caches
      clearCacheItem(`automation.schedule.${connectionId}`);
      clearCacheItem(`automation.nextRuns.${connectionId}`);
      return response;
    });
  },

  // Enable default schedule for a connection
  enableDefaultSchedule: (connectionId, timezone = null) => {
    const data = timezone ? { timezone } : {};
    return enhancedRequest({
      method: 'POST',
      url: `/automation/schedules/${connectionId}/enable-default`,
      data,
      requestId: `automation.enableDefault.${connectionId}`
    }).then(response => {
      // Clear affected caches
      clearCacheItem(`automation.schedule.${connectionId}`);
      clearCacheItem(`automation.nextRuns.${connectionId}`);
      return response;
    });
  },

  // Trigger immediate automation
  triggerImmediate: (connectionId, automationType = null) => {
    const data = automationType ? { automation_type: automationType } : {};
    return enhancedRequest({
      method: 'POST',
      url: `/automation/trigger-immediate/${connectionId}`,
      data,
      requestId: `automation.triggerImmediate.${connectionId}.${automationType || 'all'}`
    });
  },

  restartScheduler: () => {
    return enhancedRequest({
      method: 'POST',
      url: '/automation/scheduler/restart',
      requestId: 'automation.scheduler.restart'
    }).then(response => {
      // Clear scheduler status cache
      clearCacheItem('automation.scheduler.status');
      return response;
    });
  }
};

// Export default enhancedRequest for custom API calls
export default enhancedRequest;